import {
  AiAgentsSidebar,
  BillingSidebar,
  ChannelsSidebar,
  DialogsSidebar,
  KnowledgeBaseSidebar,
  LogoutSidebar,
  SettingsSidebar,
  StatisticSidebar,
  SupportSidebar,
  TeamsSmall,
} from '@shared/assets/icons/components';
import { ProfileOutlined, UserOutlined } from '@ant-design/icons';
import { Routes, buildOrganizationRoute } from '@shared/constants/routes';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';
import type { PrimaryNavigationItem } from '@shared/ui/sidebar/sidebar-primary-navigation/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const usePrimaryNavigation = (): PrimaryNavigationItem[] => {
  const { t } = useTranslation('translation');
  const currentOrganizationId = useAppSelector(authSelectors.selectCurrentOrganizationId);

  return useMemo(() => {
    const getOrganizationRoute = (path: string, legacyPath: string) =>
      currentOrganizationId ? buildOrganizationRoute(path, currentOrganizationId) : legacyPath;

    return [
      {
        route: getOrganizationRoute(Routes.organizationScope.analytics, Routes.analytics.root),
        rootRoute: Routes.analytics.root,
        icon: <StatisticSidebar />,
        heading: t('sidebar.navigation.analytics'),
        permission: 'ADMIN',
      },
      {
        route: getOrganizationRoute(Routes.organizationScope.dialogs, Routes.dialogs.root),
        rootRoute: Routes.dialogs.root,
        icon: <DialogsSidebar />,
        heading: t('sidebar.navigation.dialogs'),
        permission: 'MANAGER',
      },
      {
        route: getOrganizationRoute(Routes.organizationScope.aiAgents, Routes.aiAgents.root),
        rootRoute: Routes.aiAgents.root,
        icon: <AiAgentsSidebar />,
        heading: t('sidebar.navigation.aiAgents'),
        permission: 'ADMIN',
      },
      {
        route: getOrganizationRoute(Routes.organizationScope.channels, Routes.channels.root),
        rootRoute: Routes.channels.root,
        icon: <ChannelsSidebar />,
        heading: t('sidebar.navigation.channels'),
        permission: 'ADMIN',
      },
      {
        route: getOrganizationRoute(Routes.organizationScope.presets, Routes.presets.root),
        rootRoute: Routes.presets.root,
        icon: <SettingsSidebar />,
        heading: t('sidebar.navigation.presets'),
        permission: 'ADMIN',
      },
      {
        route: getOrganizationRoute(
          Routes.organizationScope.knowledgeBase,
          Routes.knowledgeBase.root,
        ),
        rootRoute: Routes.knowledgeBase.root,
        icon: <KnowledgeBaseSidebar />,
        heading: t('sidebar.navigation.knowledgeBase'),
        permission: 'ADMIN',
      },
      {
        route: getOrganizationRoute(Routes.organizationScope.preview, Routes.preview.root),
        rootRoute: Routes.preview.root,
        icon: <ProfileOutlined />,
        heading: t('sidebar.navigation.preview'),
        permission: 'ADMIN',
      },
      {
        route: getOrganizationRoute(Routes.organizationScope.team, Routes.team.root),
        rootRoute: Routes.team.root,
        icon: <TeamsSmall />,
        heading: t('sidebar.navigation.team'),
        permission: 'MANAGER',
      },
      {
        route: getOrganizationRoute(Routes.organizationScope.billing, Routes.billing.root),
        rootRoute: Routes.billing.root,
        icon: <BillingSidebar />,
        heading: t('sidebar.navigation.billing'),
        permission: 'OWNER',
      },
      {
        route: Routes.profile.user.root,
        rootRoute: Routes.profile.user.root,
        icon: <UserOutlined />,
        heading: t('sidebar.navigation.profile'),
        permission: 'MANAGER',
      },
      {
        route: Routes.support.root,
        rootRoute: Routes.support.root,
        icon: <SupportSidebar />,
        heading: t('sidebar.navigation.support'),
        permission: 'MANAGER',
      },
      {
        route: Routes.authentication.logOut,
        rootRoute: Routes.authentication.logOut,
        icon: <LogoutSidebar />,
        heading: t('sidebar.navigation.logOut'),
        permission: 'MANAGER',
      },
    ];
  }, [currentOrganizationId, t]);
};
